import type { User, Flight, Overlap, RosterEntry } from '../types';
import { buildRoster } from './roster';
import { dateRangeOverlap, daysBetween, toDateStr, parseISO } from './dates';

export interface StayPeriod {
  city: string;
  startDate: string;
  endDate: string;
  reason: 'layover' | 'free_at_base';
  /** Time crew arrives in city (from inbound flight), e.g. "14:30" */
  arrivalTime?: string;
  /** Time crew departs city (from outbound flight), e.g. "18:30" */
  departureTime?: string;
}

/**
 * Unified function: extract contiguous stay periods per city from a roster.
 * A person is "in" a city on a given day if:
 *   - free/layover with entry.city === city
 *   - flight with departureAirport === city (still there until departure)
 *   - flight with arrivalAirport === city (arrives there)
 * Consecutive dates (gap <= 1 day) for the same city are grouped into one StayPeriod.
 */
export function getStayPeriods(roster: RosterEntry[], baseAirport?: string): StayPeriod[] {
  // Collect all unique cities referenced in the roster
  const cities = new Set<string>();
  for (const e of roster) {
    cities.add(e.city);
    if (e.type === 'flight' && e.flight) {
      cities.add(e.flight.departureAirport);
      cities.add(e.flight.arrivalAirport);
    }
  }

  const allStays: StayPeriod[] = [];

  for (const city of cities) {
    // Find entries where person is in this city
    const entries = roster.filter((e) => {
      if ((e.type === 'layover' || e.type === 'free') && e.city === city) return true;
      if (e.type === 'flight' && e.flight?.arrivalAirport === city) return true;
      // Departure day: include for layover cities (you're there until you leave),
      // but EXCLUDE for the user's base (you're leaving home, not "at home").
      // This creates visible gaps in the home-base Gantt for turnaround trips.
      if (e.type === 'flight' && e.flight?.departureAirport === city && city !== baseAirport) return true;
      return false;
    });
    if (entries.length === 0) continue;

    // Group consecutive dates (gap <= 1 day)
    type Acc = { startDate: string; endDate: string; entries: RosterEntry[] };
    const groups: Acc[] = [];
    let cur: Acc = { startDate: entries[0].date, endDate: entries[0].date, entries: [entries[0]] };

    for (let i = 1; i < entries.length; i++) {
      const prevDate = parseISO(cur.endDate);
      const currDate = parseISO(entries[i].date);
      const diff = Math.round((currDate.getTime() - prevDate.getTime()) / 86400000);
      if (diff <= 1) {
        cur.endDate = entries[i].date;
        cur.entries.push(entries[i]);
      } else {
        groups.push(cur);
        cur = { startDate: entries[i].date, endDate: entries[i].date, entries: [entries[i]] };
      }
    }
    groups.push(cur);

    for (const g of groups) {
      const reason: 'layover' | 'free_at_base' =
        g.entries.some((e) => e.type === 'layover' || e.type === 'flight')
          ? 'layover'
          : 'free_at_base';

      // arrivalTime: from first flight where arrivalAirport === city
      let arrivalTime: string | undefined;
      for (const e of g.entries) {
        if (e.type === 'flight' && e.flight?.arrivalAirport === city && e.flight.arrivalTime) {
          arrivalTime = e.flight.arrivalTime;
          break;
        }
      }

      // departureTime: from last flight where departureAirport === city
      let departureTime: string | undefined;
      for (let i = g.entries.length - 1; i >= 0; i--) {
        const e = g.entries[i];
        if (e.type === 'flight' && e.flight?.departureAirport === city && e.flight.departureTime) {
          departureTime = e.flight.departureTime;
          break;
        }
      }

      allStays.push({ city, startDate: g.startDate, endDate: g.endDate, reason, arrivalTime, departureTime });
    }
  }

  // Sort by startDate
  allStays.sort((a, b) => a.startDate.localeCompare(b.startDate));
  return allStays;
}

export interface CityOverlapGroup {
  city: string;
  /** Earliest date anyone (including you) is in the city */
  rangeStart: string;
  /** Latest date anyone is in the city */
  rangeEnd: string;
  /** Your stay(s) — may be multiple if you visit the same city twice */
  myStays: StayPeriod[];
  /** Each friend's stay (one entry per contiguous stay period) */
  friendStays: FriendStay[];
  /** Dates where 3+ people (you + 2+ friends) overlap */
  groupDates: string[];
  /** Total overlap hours (union of all friend overlaps, time-aware) */
  totalOverlapHours: number;
}

export interface FriendStay {
  user: User;
  airlineCode: string;
  startDate: string;
  endDate: string;
  reason: 'layover' | 'free_at_base';
  arrivalTime?: string;
  departureTime?: string;
  overlapWithMe: {
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
    days: number;
    hours: number;
  };
}

/** Convert a date + optional HH:mm time to epoch ms */
function toTimestamp(dateStr: string, time?: string, isEnd?: boolean): number {
  const d = parseISO(dateStr);
  if (time) {
    const [h, m] = time.split(':').map(Number);
    d.setHours(h || 0, m || 0, 0, 0);
  } else {
    d.setHours(isEnd ? 23 : 0, isEnd ? 59 : 0, 0, 0);
  }
  return d.getTime();
}

function formatHHmm(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Generate all date strings in a range (inclusive) */
function datesInRange(start: string, end: string): string[] {
  const dates: string[] = [];
  const d = parseISO(start);
  const endD = parseISO(end);
  while (d <= endD) {
    dates.push(toDateStr(d));
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

/**
 * Group overlaps by city AND trip. Each of your contiguous stays in a city
 * becomes its own group, so visiting BKK twice shows two separate Gantt cards.
 */
export function groupOverlapsByCity(
  currentUser: User,
  currentUserFlights: Flight[],
  allFlights: Flight[],
  overlaps: Overlap[],
  month: number,
  year: number
): CityOverlapGroup[] {
  if (overlaps.length === 0) return [];

  const cities = [...new Set(overlaps.map((o) => o.city))];
  const myRoster = buildRoster(currentUserFlights, currentUser, month, year);
  const myAllStays = getStayPeriods(myRoster, currentUser.baseAirport);

  const groups: CityOverlapGroup[] = [];

  for (const city of cities) {
    const cityOverlaps = overlaps.filter((o) => o.city === city);
    if (cityOverlaps.length === 0) continue;

    const myStays = myAllStays.filter((s) => s.city === city);
    if (myStays.length === 0) continue;

    // Collect unique friends for this city
    const friendMap = new Map<string, User>();
    for (const o of cityOverlaps) {
      for (const f of o.friends) {
        if (!friendMap.has(f.user.id)) friendMap.set(f.user.id, f.user);
      }
    }

    // Pre-compute friend stay periods for this city (shared across my trips)
    const allFriendPeriods: { user: User; period: StayPeriod }[] = [];
    for (const [, friendUser] of friendMap) {
      const friendFlights = allFlights.filter((f) => f.userId === friendUser.id);
      const friendRoster = buildRoster(friendFlights, friendUser, month, year);
      for (const period of getStayPeriods(friendRoster, friendUser.baseAirport).filter((s) => s.city === city)) {
        allFriendPeriods.push({ user: friendUser, period });
      }
    }

    // Create one group per MY stay (each trip = its own card)
    for (const myStay of myStays) {
      // Skip short turnarounds (<6h ground time) — not a real meetup opportunity
      const stayStartTs = toTimestamp(myStay.startDate, myStay.arrivalTime);
      const stayEndTs = toTimestamp(myStay.endDate, myStay.departureTime, true);
      const stayHours = (stayEndTs - stayStartTs) / 3600000;
      if (stayHours < 6) continue;

      const friendStays: FriendStay[] = [];
      const rangeStart = myStay.startDate;
      const rangeEnd = myStay.endDate;

      for (const { user: friendUser, period: fPeriod } of allFriendPeriods) {
        const overlapRange = dateRangeOverlap(
          myStay.startDate, myStay.endDate,
          fPeriod.startDate, fPeriod.endDate
        );
        if (!overlapRange) continue;

        // Time-aware overlap calculation
        const myStartTs = toTimestamp(myStay.startDate, myStay.arrivalTime);
        const myEndTs = toTimestamp(myStay.endDate, myStay.departureTime, true);
        const fStartTs = toTimestamp(fPeriod.startDate, fPeriod.arrivalTime);
        const fEndTs = toTimestamp(fPeriod.endDate, fPeriod.departureTime, true);
        const oStartTs = Math.max(myStartTs, fStartTs);
        const oEndTs = Math.min(myEndTs, fEndTs);
        const overlapHours = Math.max(0, Math.round((oEndTs - oStartTs) / 3600000));

        friendStays.push({
          user: friendUser,
          airlineCode: friendUser.airlineCode,
          startDate: fPeriod.startDate,
          endDate: fPeriod.endDate,
          reason: fPeriod.reason,
          arrivalTime: fPeriod.arrivalTime,
          departureTime: fPeriod.departureTime,
          overlapWithMe: {
            startDate: toDateStr(new Date(oStartTs)),
            endDate: toDateStr(new Date(oEndTs)),
            startTime: formatHHmm(oStartTs),
            endTime: formatHHmm(oEndTs),
            days: daysBetween(overlapRange.start, overlapRange.end),
            hours: overlapHours,
          },
        });
      }

      if (friendStays.length === 0) continue;

      // Find 3-way+ dates
      const groupDates: string[] = [];
      for (const dateStr of datesInRange(myStay.startDate, myStay.endDate)) {
        const friendsOnDay = friendStays.filter(
          (fs) => dateStr >= fs.startDate && dateStr <= fs.endDate
        );
        if (friendsOnDay.length >= 2) groupDates.push(dateStr);
      }

      // Total overlap hours
      const intervals: [number, number][] = friendStays.map((fs) => [
        toTimestamp(fs.overlapWithMe.startDate, fs.overlapWithMe.startTime),
        toTimestamp(fs.overlapWithMe.endDate, fs.overlapWithMe.endTime, true),
      ]);
      intervals.sort((a, b) => a[0] - b[0]);
      const merged: [number, number][] = [];
      for (const [s, e] of intervals) {
        if (merged.length && s <= merged[merged.length - 1][1]) {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
        } else {
          merged.push([s, e]);
        }
      }
      const totalMs = merged.reduce((sum, [s, e]) => sum + (e - s), 0);

      groups.push({
        city,
        rangeStart,
        rangeEnd,
        myStays: [myStay],
        friendStays,
        groupDates,
        totalOverlapHours: Math.max(1, Math.round(totalMs / 3600000)),
      });
    }
  }

  // Merge all home-base groups into one unified group per month.
  const baseCity = currentUser.baseAirport;
  if (baseCity) {
    const homeGroups = groups.filter((g) => g.city === baseCity);
    const nonHomeGroups = groups.filter((g) => g.city !== baseCity);

    if (homeGroups.length > 1) {
      const allMyHomeStays = homeGroups.flatMap((g) => g.myStays);
      const allFriendStays = homeGroups.flatMap((g) => g.friendStays);

      // Deduplicate friend stays (same user + same overlap range may appear in multiple groups)
      const seenFS = new Set<string>();
      const dedupedFriendStays = allFriendStays.filter((fs) => {
        const key = `${fs.user.id}:${fs.overlapWithMe.startDate}:${fs.overlapWithMe.endDate}`;
        if (seenFS.has(key)) return false;
        seenFS.add(key);
        return true;
      });

      // Recompute total overlap hours from merged friend stays
      const intervals: [number, number][] = dedupedFriendStays.map((fs) => [
        toTimestamp(fs.overlapWithMe.startDate, fs.overlapWithMe.startTime),
        toTimestamp(fs.overlapWithMe.endDate, fs.overlapWithMe.endTime, true),
      ]);
      intervals.sort((a, b) => a[0] - b[0]);
      const merged: [number, number][] = [];
      for (const [s, e] of intervals) {
        if (merged.length && s <= merged[merged.length - 1][1]) {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
        } else {
          merged.push([s, e]);
        }
      }
      const totalMs = merged.reduce((sum, [s, e]) => sum + (e - s), 0);

      // Recompute 3-way+ group dates across all home stays
      const allMyHomeDates = allMyHomeStays.flatMap((s) => datesInRange(s.startDate, s.endDate));
      const recomputedGroupDates: string[] = [];
      for (const dateStr of allMyHomeDates) {
        const uniqueFriendsOnDay = new Set(
          dedupedFriendStays
            .filter((fs) => dateStr >= fs.startDate && dateStr <= fs.endDate)
            .map((fs) => fs.user.id)
        );
        if (uniqueFriendsOnDay.size >= 2) recomputedGroupDates.push(dateStr);
      }

      const mergedHome: CityOverlapGroup = {
        city: baseCity,
        rangeStart: allMyHomeStays.reduce((min, s) => s.startDate < min ? s.startDate : min, allMyHomeStays[0].startDate),
        rangeEnd: allMyHomeStays.reduce((max, s) => s.endDate > max ? s.endDate : max, allMyHomeStays[0].endDate),
        myStays: allMyHomeStays,
        friendStays: dedupedFriendStays,
        groupDates: recomputedGroupDates,
        totalOverlapHours: Math.max(1, Math.round(totalMs / 3600000)),
      };

      groups.length = 0;
      groups.push(...nonHomeGroups, mergedHome);
    }
  }

  groups.sort((a, b) => a.myStays[0].startDate.localeCompare(b.myStays[0].startDate));

  return groups;
}
